import { ColliClient } from '@frontend/colli/api';
import { Colli } from '@frontend/colli/types';
import { Logger } from '@frontend/logger';
import { MobileWizardStateRecord, updateData } from '@frontend/wizard/mobile';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppDispatch } from '../../redux/store';
import { UseWizardTransactionReturn } from './useWizardTransaction';

/**
 * Colli synchronization states:
 * - INIT: No collis yet / not yet created on backend
 * - AHEAD: Local changes exist that haven't been synced to backend
 * - SYNCED: Local and backend are in sync
 */
export enum ColliSyncState {
    INIT = 'init',
    AHEAD = 'ahead',
    SYNCED = 'synced'
}

export interface UseWizardTransactionCollisOptions {
    wizardId?: string;
    store: MobileWizardStateRecord | null;

    /**
     * The transaction ID to scope all colli operations to.
     */
    transactionId: string;

    /**
     * Optional transaction context — used only for account_id fallback
     */
    transactionContext?: UseWizardTransactionReturn;

    /**
     * Auto-fetch collis for this transaction on mount
     * @default true
     */
    autoFetch?: boolean;

    /**
     * Callback fired when collis are created
     */
    onCreate?: (collis: Colli[]) => void | Promise<void>;
}

export interface UseWizardTransactionCollisReturn {
    /** Current collis data (merged local + backend) for this transaction */
    collis: Partial<Colli>[];
    remoteCollis: Colli[];

    /** Loading state for async operations */
    isLoading: boolean;

    /** Current synchronization state */
    colliState: ColliSyncState;

    addColli: (colliData: Partial<Colli>) => Promise<void>;
    updateColli: (indexOrId: number | string, data: Partial<Colli>) => Promise<void>;
    removeColli: (indexOrId: number | string) => Promise<void>;
    setCollis: (collis: Partial<Colli>[]) => Promise<void>;

    /**
     * Create/update collis on backend
     * Transitions state from INIT|AHEAD to SYNCED
     */
    syncCollis: () => Promise<void>;

    refresh: () => Promise<void>;
    clearCollis: () => Promise<void>;
}

/**
 * Hook for managing wizard collis scoped to a single transaction.
 */
export const useWizardTransactionCollis = (options: UseWizardTransactionCollisOptions): UseWizardTransactionCollisReturn => {
    const dispatch = useAppDispatch();
    const { state: locationState } = useLocation();

    const [isLoading, setIsLoading] = useState(false);
    const [colliState, setColliState] = useState<ColliSyncState>(ColliSyncState.INIT);
    const [remoteCollis, setRemoteCollis] = useState<Colli[]>([]);

    const isMountedRef = useRef(true);

    const { wizardId, store, transactionId, transactionContext, autoFetch = true } = options;

    const localCollis = (store?.data?.collis as Partial<Colli>[]) || [];
    const accountId = store?.wizard.account_id || transactionContext?.transaction?.account_id;

    // Seed wizard store from React Router location state on mount
    useEffect(() => {
        if (wizardId && !localCollis.length && locationState?.collis) {
            dispatch(updateData({ key: wizardId, data: { collis: locationState.collis } }));
        }
    }, []);

    const storeRef = useRef(store);
    useEffect(() => {
        storeRef.current = store;
    }, [store]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const waitForStoreUpdate = async (checkFn: () => boolean, maxAttempts = 50, delayMs = 10) => {
        for (let i = 0; i < maxAttempts; i++) {
            if (checkFn()) return true;
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        return false;
    };

    useEffect(() => {
        if (!autoFetch || !store || !accountId || !wizardId || !transactionId) return;

        const initializeCollis = async () => {
            setIsLoading(true);
            try {
                const response = await ColliClient.fetchCollis({ transaction_id: transactionId });
                const fetchedCollis = response.results || [];

                if (fetchedCollis.length > 0 && isMountedRef.current) {
                    setRemoteCollis(fetchedCollis);

                    if (localCollis.length === 0) {
                        await dispatch(updateData({ key: wizardId, data: { collis: fetchedCollis } }));
                        setColliState(ColliSyncState.SYNCED);
                    } else {
                        const hasLocalChanges = hasCollisChanged(localCollis, fetchedCollis);
                        setColliState(hasLocalChanges ? ColliSyncState.AHEAD : ColliSyncState.SYNCED);
                    }
                } else {
                    setColliState(ColliSyncState.INIT);
                }
            } catch (error) {
                Logger.error('Failed to initialize collis:', error);
                setColliState(ColliSyncState.INIT);
            } finally {
                if (isMountedRef.current) setIsLoading(false);
            }
        };

        initializeCollis();
    }, [wizardId, autoFetch, accountId, transactionId]);

    const addColli = async (colliData: Partial<Colli>) => {
        if (!store || !wizardId) {
            Logger.warn('Cannot add colli: wizard store not available');
            return;
        }

        const currentCollis = (storeRef.current?.data?.collis as Partial<Colli>[]) || [];
        const newColli: Partial<Colli> = { ...colliData, transaction_id: transactionId, account_id: colliData.account_id || accountId };
        const expectedLength = currentCollis.length + 1;

        await dispatch(updateData({ key: wizardId, data: { collis: [...currentCollis, newColli] } }));
        if (colliState === ColliSyncState.SYNCED) setColliState(ColliSyncState.AHEAD);

        await waitForStoreUpdate(() => ((storeRef.current?.data?.collis as Partial<Colli>[]) || []).length === expectedLength);
    };

    const updateColli = async (indexOrId: number | string, data: Partial<Colli>) => {
        if (!store || !wizardId) {
            Logger.warn('Cannot update colli: wizard store not available');
            return;
        }

        const updatedCollis = [...localCollis];
        const targetIndex = typeof indexOrId === 'number' ? indexOrId : updatedCollis.findIndex((c) => c.id === indexOrId);

        if (targetIndex === -1 || targetIndex >= updatedCollis.length) {
            Logger.warn('Colli not found:', indexOrId);
            return;
        }

        updatedCollis[targetIndex] = { ...updatedCollis[targetIndex], ...data };
        await dispatch(updateData({ key: wizardId, data: { collis: updatedCollis } }));
        if (colliState === ColliSyncState.SYNCED) setColliState(ColliSyncState.AHEAD);
    };

    const removeColli = async (indexOrId: number | string) => {
        if (!store || !wizardId) {
            Logger.warn('Cannot remove colli: wizard store not available');
            return;
        }

        const updatedCollis = typeof indexOrId === 'number' ? localCollis.filter((_, idx) => idx !== indexOrId) : localCollis.filter((c) => c.id !== indexOrId);

        await dispatch(updateData({ key: wizardId, data: { collis: updatedCollis } }));
        if (colliState === ColliSyncState.SYNCED) setColliState(ColliSyncState.AHEAD);
    };

    const setCollis = async (collis: Partial<Colli>[]) => {
        if (!store || !wizardId) {
            Logger.warn('Cannot set collis: wizard store not available');
            return;
        }

        await dispatch(updateData({ key: wizardId, data: { collis } }));
        if (colliState === ColliSyncState.SYNCED) setColliState(ColliSyncState.AHEAD);
    };

    const syncCollis = async (): Promise<void> => {
        const currentStore = storeRef.current;
        const currentLocalCollis = (currentStore?.data?.collis as Partial<Colli>[]) || [];
        const currentAccountId = currentStore?.wizard.account_id || transactionContext?.transaction?.account_id;

        if (!currentStore || !currentAccountId) {
            throw new Error('Cannot sync collis: missing account ID');
        }
        if (currentLocalCollis.length === 0) {
            throw new Error('Cannot sync collis: no collis to sync');
        }

        setIsLoading(true);
        try {
            const createdCollis: Colli[] = [];
            const updatedCollis: Colli[] = [];
            const deletedColliIds: string[] = [];

            for (const colli of currentLocalCollis) {
                if (!colli.id) {
                    const created = await ColliClient.postColli(currentAccountId, transactionId, colli);
                    createdCollis.push(created);
                } else {
                    const remoteColli = remoteCollis.find((r) => r.id === colli.id);
                    if (remoteColli && hasColliChanged(colli, remoteColli)) {
                        const updated = await ColliClient.patchColli(currentAccountId, transactionId, colli.id, colli);
                        updatedCollis.push(updated);
                    }
                }
            }

            for (const remoteColli of remoteCollis) {
                if (!currentLocalCollis.find((l) => l.id === remoteColli.id)) {
                    await ColliClient.deleteColli(currentAccountId, transactionId, remoteColli.id);
                    deletedColliIds.push(remoteColli.id);
                }
            }

            const syncedCollis: Colli[] = [
                ...remoteCollis.filter((r) => !updatedCollis.find((u) => u.id === r.id) && !deletedColliIds.includes(r.id)),
                ...createdCollis,
                ...updatedCollis
            ];

            setRemoteCollis(syncedCollis);

            if (wizardId) {
                await dispatch(updateData({ key: wizardId, data: { collis: syncedCollis } }));
            }

            setColliState(ColliSyncState.SYNCED);

            if (options.onCreate && createdCollis.length > 0) {
                await options.onCreate(createdCollis);
            }
        } catch (error) {
            Logger.error('Failed to sync collis:', error);
            throw error;
        } finally {
            if (isMountedRef.current) setIsLoading(false);
        }
    };

    const refresh = async (): Promise<void> => {
        if (!accountId) throw new Error('Cannot refresh collis: no account ID');

        setIsLoading(true);
        try {
            const response = await ColliClient.fetchCollis({ transaction_id: transactionId });
            const refreshedCollis = response.results || [];

            if (isMountedRef.current) {
                setRemoteCollis(refreshedCollis);
                if (wizardId) await dispatch(updateData({ key: wizardId, data: { collis: refreshedCollis } }));
                setColliState(ColliSyncState.SYNCED);
            }
        } catch (error) {
            Logger.error('Failed to refresh collis:', error);
            throw error;
        } finally {
            if (isMountedRef.current) setIsLoading(false);
        }
    };

    const clearCollis = async () => {
        if (!store || !wizardId) return;
        await dispatch(updateData({ key: wizardId, data: { collis: [] } }));
        setColliState(remoteCollis.length > 0 ? ColliSyncState.AHEAD : ColliSyncState.INIT);
    };

    return { collis: localCollis, remoteCollis, isLoading, colliState, addColli, updateColli, removeColli, setCollis, syncCollis, refresh, clearCollis };
};

function hasCollisChanged(localCollis: Partial<Colli>[], remoteCollis: Colli[]): boolean {
    if (localCollis.length !== remoteCollis.length) return true;
    for (const local of localCollis) {
        if (!local.id) return true;
        const remote = remoteCollis.find((r) => r.id === local.id);
        if (!remote || hasColliChanged(local, remote)) return true;
    }
    return false;
}

function hasColliChanged(local: Partial<Colli>, remote: Colli): boolean {
    const compareFields: (keyof Colli)[] = ['label', 'index', 'type', 'state', 'notes', 'workflow_id'];
    return compareFields.some((field) => local[field] !== undefined && JSON.stringify(local[field]) !== JSON.stringify(remote[field]));
}
